import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import semesterService from "../../services/semesterService";

export const fetchSemesters = createAsyncThunk(
  "semesters/fetchSemesters",
  async (params, { rejectWithValue }) => {
    try {
      const response = await semesterService.getAllSemesters(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch semesters"
      );
    }
  }
);

export const createSemester = createAsyncThunk(
  "semesters/createSemester",
  async (data, { rejectWithValue }) => {
    try {
      const response = await semesterService.createSemester(data);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create semester"
      );
    }
  }
);

export const updateSemester = createAsyncThunk(
  "semesters/updateSemester",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await semesterService.updateSemester(id, data);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update semester"
      );
    }
  }
);

export const deleteSemester = createAsyncThunk(
  "semesters/deleteSemester",
  async (id, { rejectWithValue }) => {
    try {
      const response = await semesterService.deleteSemester(id);
      return { id, response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete semester"
      );
    }
  }
);

const semesterSlice = createSlice({
  name: "semesters",
  initialState: {
    semesters: [],
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    error: null,
    success: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSemesters.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSemesters.fulfilled, (state, action) => {
        state.isLoading = false;
        const responseData = action.payload.data || action.payload;
        state.semesters = responseData.semesters || responseData || [];
      })
      .addCase(fetchSemesters.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createSemester.pending, (state) => {
        state.isCreating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createSemester.fulfilled, (state, action) => {
        state.isCreating = false;
        const responseData = action.payload.data || action.payload;
        const newSemester = responseData.semester || responseData;
        if (newSemester) {
          state.semesters.unshift(newSemester);
        }
        state.success = action.payload.message || "Semester created successfully";
      })
      .addCase(createSemester.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload;
      })
      .addCase(updateSemester.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateSemester.fulfilled, (state, action) => {
        state.isUpdating = false;
        const responseData = action.payload.data || action.payload;
        const updatedSemester = responseData.semester || responseData;
        const index = state.semesters.findIndex((s) => s.id === updatedSemester.id);
        if (index !== -1) {
          state.semesters[index] = updatedSemester;
        }
        state.success = action.payload.message || "Semester updated successfully";
      })
      .addCase(updateSemester.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })
      .addCase(deleteSemester.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteSemester.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.semesters = state.semesters.filter((s) => s.id !== action.payload.id);
        state.success = action.payload.response.message || "Semester deleted successfully";
      })
      .addCase(deleteSemester.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearSuccess } = semesterSlice.actions;
export default semesterSlice.reducer;
